// ErrorBoundary — top-level uncaught error catch.
//
// Renders a minimal fallback page with a Reload button when a render-time
// error escapes any component. Catches programmer errors, not promise
// rejections (TanStack Query handles those via its own error state).
//
// File-level i18n-rule disable: the boundary is the last line of defense
// when ANYTHING (including i18n init) failed; it must work with literal
// English fallbacks. Reading from i18next here would risk re-erroring.
/* eslint-disable i18next/no-literal-string */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(_err: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // No logger primitive yet; surface to devtools console for now.
    // A frontend log shipper is future work.
    // eslint-disable-next-line no-console
    console.error('Uncaught render error', error, info);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render() {
    if (this.state.hasError) {
      // Pre-i18n fallback — i18n itself may have failed, so keep the
      // boundary self-contained with English fallbacks. Standard practice
      // for top-level boundaries (must work even when i18n broke).
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 bg-[var(--color-bg-page)] text-[var(--color-text-primary)]">
          <h1 className="text-xl font-bold">Something went wrong</h1>
          <p className="text-sm text-[var(--color-text-secondary)] text-center max-w-md">
            The application encountered an unexpected error. Reload the page to try again.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="px-4 py-2 min-h-[2.5rem] text-sm font-medium rounded-md bg-[var(--color-primary-display)] text-[var(--color-on-primary-display)] hover:brightness-110 outline-none focus-ring"
          >
            Reload
          </button>
        </div>
      );
    }
    return <>{this.props.children}</>;
  }
}
